import { useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { GraduationCap, Users, TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function FormField({ label, children }) {
  return (
    <div className="space-y-2">
      <label className="block text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-body">
        {label}
      </label>
      {children}
    </div>
  );
}

const inputClass = "bg-transparent border-[#262626] text-white placeholder:text-[#555] focus:border-[#FF3B30] focus:ring-0 rounded-none font-body";
const btnClass = "w-full bg-[#FF3B30] text-white font-heading uppercase tracking-widest px-8 py-4 text-sm hover:bg-[#FF5C53] transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed";

function StudentForm() {
  const { t } = useLang();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", age: "", nationality: "",
    discipline: "", experience_level: "", why_good_fit: "",
    parent_name: "", parent_email: "", phone: "",
  });

  const update = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.age || !form.discipline || !form.why_good_fit) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/signups/student`, { ...form, age: parseInt(form.age) });
      toast.success(t.forms.student.success);
      setForm({ full_name: "", email: "", age: "", nationality: "", discipline: "", experience_level: "", why_good_fit: "", parent_name: "", parent_email: "", phone: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="student-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <FormField label={t.forms.student.fullName + " *"}>
          <Input data-testid="student-fullname" className={inputClass} value={form.full_name} onChange={(e) => update("full_name", e.target.value)} placeholder="John Doe" />
        </FormField>
        <FormField label={t.forms.student.email + " *"}>
          <Input data-testid="student-email" type="email" className={inputClass} value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="john@example.com" />
        </FormField>
        <FormField label={t.forms.student.age + " *"}>
          <Input data-testid="student-age" type="number" min="10" max="25" className={inputClass} value={form.age} onChange={(e) => update("age", e.target.value)} placeholder="16" />
        </FormField>
        <FormField label={t.forms.student.nationality}>
          <Input data-testid="student-nationality" className={inputClass} value={form.nationality} onChange={(e) => update("nationality", e.target.value)} placeholder="Spanish" />
        </FormField>
        <FormField label={t.forms.student.discipline + " *"}>
          <Select value={form.discipline} onValueChange={(v) => update("discipline", v)}>
            <SelectTrigger data-testid="student-discipline" className={inputClass}><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent className="bg-[#111111] border-[#262626] text-white">
              {Object.entries(t.forms.disciplines).map(([k, v]) => (
                <SelectItem key={k} value={k} className="hover:bg-[#262626] focus:bg-[#262626] focus:text-white">{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label={t.forms.student.experience}>
          <Select value={form.experience_level} onValueChange={(v) => update("experience_level", v)}>
            <SelectTrigger data-testid="student-experience" className={inputClass}><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent className="bg-[#111111] border-[#262626] text-white">
              {Object.entries(t.forms.experienceLevels).map(([k, v]) => (
                <SelectItem key={k} value={k} className="hover:bg-[#262626] focus:bg-[#262626] focus:text-white">{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      </div>
      <FormField label={t.forms.student.whyFit + " *"}>
        <Textarea data-testid="student-whyfit" className={`${inputClass} min-h-[120px]`} value={form.why_good_fit} onChange={(e) => update("why_good_fit", e.target.value)} placeholder="Tell us about yourself..." />
      </FormField>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <FormField label={t.forms.student.parentName}>
          <Input data-testid="student-parentname" className={inputClass} value={form.parent_name} onChange={(e) => update("parent_name", e.target.value)} />
        </FormField>
        <FormField label={t.forms.student.parentEmail}>
          <Input data-testid="student-parentemail" type="email" className={inputClass} value={form.parent_email} onChange={(e) => update("parent_email", e.target.value)} />
        </FormField>
        <FormField label={t.forms.student.phone}>
          <Input data-testid="student-phone" className={inputClass} value={form.phone} onChange={(e) => update("phone", e.target.value)} />
        </FormField>
      </div>
      <button type="submit" data-testid="student-submit-btn" className={btnClass} disabled={loading}>
        {loading ? "..." : t.forms.student.submit}
      </button>
    </form>
  );
}

function CoachForm() {
  const { t } = useLang();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", specialty: "",
    experience_years: "", certifications: "", why_good_fit: "", resume_link: "",
  });

  const update = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.specialty || !form.why_good_fit) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/signups/coach`, { ...form, experience_years: parseInt(form.experience_years) || 0 });
      toast.success(t.forms.coach.success);
      setForm({ full_name: "", email: "", phone: "", specialty: "", experience_years: "", certifications: "", why_good_fit: "", resume_link: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="coach-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <FormField label={t.forms.coach.fullName + " *"}>
          <Input data-testid="coach-fullname" className={inputClass} value={form.full_name} onChange={(e) => update("full_name", e.target.value)} placeholder="Jane Smith" />
        </FormField>
        <FormField label={t.forms.coach.email + " *"}>
          <Input data-testid="coach-email" type="email" className={inputClass} value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="jane@example.com" />
        </FormField>
        <FormField label={t.forms.coach.phone}>
          <Input data-testid="coach-phone" className={inputClass} value={form.phone} onChange={(e) => update("phone", e.target.value)} />
        </FormField>
        <FormField label={t.forms.coach.specialty + " *"}>
          <Select value={form.specialty} onValueChange={(v) => update("specialty", v)}>
            <SelectTrigger data-testid="coach-specialty" className={inputClass}><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent className="bg-[#111111] border-[#262626] text-white">
              {Object.entries(t.forms.disciplines).map(([k, v]) => (
                <SelectItem key={k} value={k} className="hover:bg-[#262626] focus:bg-[#262626] focus:text-white">{v}</SelectItem>
              ))}
              <SelectItem value="academics" className="hover:bg-[#262626] focus:bg-[#262626] focus:text-white">Academics / Teaching</SelectItem>
              <SelectItem value="fitness" className="hover:bg-[#262626] focus:bg-[#262626] focus:text-white">Fitness / Conditioning</SelectItem>
            </SelectContent>
          </Select>
        </FormField>
        <FormField label={t.forms.coach.experience}>
          <Input data-testid="coach-experience" type="number" min="0" className={inputClass} value={form.experience_years} onChange={(e) => update("experience_years", e.target.value)} placeholder="5" />
        </FormField>
        <FormField label={t.forms.coach.certifications}>
          <Input data-testid="coach-certifications" className={inputClass} value={form.certifications} onChange={(e) => update("certifications", e.target.value)} placeholder="UCI Level 2, etc." />
        </FormField>
      </div>
      <FormField label={t.forms.coach.whyFit + " *"}>
        <Textarea data-testid="coach-whyfit" className={`${inputClass} min-h-[120px]`} value={form.why_good_fit} onChange={(e) => update("why_good_fit", e.target.value)} placeholder="Tell us about your coaching philosophy..." />
      </FormField>
      <FormField label={t.forms.coach.resumeLink}>
        <Input data-testid="coach-resume" className={inputClass} value={form.resume_link} onChange={(e) => update("resume_link", e.target.value)} placeholder="https://..." />
      </FormField>
      <button type="submit" data-testid="coach-submit-btn" className={btnClass} disabled={loading}>
        {loading ? "..." : t.forms.coach.submit}
      </button>
    </form>
  );
}

function InvestorForm() {
  const { t } = useLang();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", company: "",
    message: "", investment_interest: "",
  });

  const update = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contacts/investor`, form);
      toast.success(t.forms.investor.success);
      setForm({ full_name: "", email: "", phone: "", company: "", message: "", investment_interest: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="investor-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <FormField label={t.forms.investor.fullName + " *"}>
          <Input data-testid="investor-fullname" className={inputClass} value={form.full_name} onChange={(e) => update("full_name", e.target.value)} placeholder="John Doe" />
        </FormField>
        <FormField label={t.forms.investor.email + " *"}>
          <Input data-testid="investor-email" type="email" className={inputClass} value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="john@capital.com" />
        </FormField>
        <FormField label={t.forms.investor.phone}>
          <Input data-testid="investor-phone" className={inputClass} value={form.phone} onChange={(e) => update("phone", e.target.value)} />
        </FormField>
        <FormField label={t.forms.investor.company}>
          <Input data-testid="investor-company" className={inputClass} value={form.company} onChange={(e) => update("company", e.target.value)} placeholder="Capital Group" />
        </FormField>
      </div>
      <FormField label={t.forms.investor.interest}>
        <Input data-testid="investor-interest" className={inputClass} value={form.investment_interest} onChange={(e) => update("investment_interest", e.target.value)} placeholder="Seed round, sponsorship, etc." />
      </FormField>
      <FormField label={t.forms.investor.message + " *"}>
        <Textarea data-testid="investor-message" className={`${inputClass} min-h-[120px]`} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us about your interest..." />
      </FormField>
      <button type="submit" data-testid="investor-submit-btn" className={btnClass} disabled={loading}>
        {loading ? "..." : t.forms.investor.submit}
      </button>
    </form>
  );
}

export default function SignupForms() {
  const { t } = useLang();

  return (
    <section
      id="forms"
      data-testid="forms-section"
      className="py-24 sm:py-32 bg-[#0A0A0A]"
    >
      <div className="max-w-3xl mx-auto px-6">
        {/* Section Label */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-[2px] bg-[#FF3B30]" />
          <span className="text-xs uppercase tracking-[0.3em] text-[#A3A3A3] font-body">
            {t.forms.label}
          </span>
        </div>

        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight text-white mb-12">
          {t.forms.title}
        </h2>

        <Tabs defaultValue="student" className="w-full">
          <TabsList className="w-full bg-[#111111] border border-[#262626] rounded-none h-auto p-0 grid grid-cols-3">
            <TabsTrigger
              value="student"
              data-testid="tab-student"
              className="rounded-none py-4 text-[#A3A3A3] data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white font-heading uppercase tracking-wider text-xs sm:text-sm flex items-center gap-2 transition-colors"
            >
              <GraduationCap className="w-4 h-4" />
              {t.forms.studentTab}
            </TabsTrigger>
            <TabsTrigger
              value="coach"
              data-testid="tab-coach"
              className="rounded-none py-4 text-[#A3A3A3] data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white font-heading uppercase tracking-wider text-xs sm:text-sm flex items-center gap-2 transition-colors"
            >
              <Users className="w-4 h-4" />
              {t.forms.coachTab}
            </TabsTrigger>
            <TabsTrigger
              value="investor"
              data-testid="tab-investor"
              className="rounded-none py-4 text-[#A3A3A3] data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white font-heading uppercase tracking-wider text-xs sm:text-sm flex items-center gap-2 transition-colors"
            >
              <TrendingUp className="w-4 h-4" />
              {t.forms.investorTab}
            </TabsTrigger>
          </TabsList>

          <div className="mt-8 bg-[#111111] border border-[#262626] p-6 sm:p-8">
            <TabsContent value="student"><StudentForm /></TabsContent>
            <TabsContent value="coach"><CoachForm /></TabsContent>
            <TabsContent value="investor"><InvestorForm /></TabsContent>
          </div>
        </Tabs>
      </div>
    </section>
  );
}
